import { Table, Button, Space } from '@/shared/ui/primitives';
import { DeleteOutlined } from '@/shared/ui/icons';
import { formatDateTime } from '@/shared/utils/formatters';
import { useTranslation } from 'react-i18next';
import type { ColumnsType, TableProps } from '@/shared/ui/subpath-types';

interface Props {
  data: any[];
  loading: boolean;
  sortField: string;
  sortDir: string;
  onSort: (field: string) => void;
  onDelete: (item: any) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

export default function BlockedUsersTable({ data, loading, sortField, sortDir, onSort, onDelete, page, pageSize, total, onPageChange }: Props) {
  const { t } = useTranslation();

  const columns: ColumnsType<any> = [
    { title: t('common.id'), dataIndex: 'id', sorter: true, width: 80 },
    {
      title: t('blockedUsers.blockedBy'), dataIndex: 'blockedById', sorter: true,
      render: (_: any, record: any) => (
        <>
          <span style={{ fontWeight: 500 }}>{record.blockedByName || '—'}</span>
          <span style={{ color: '#999', marginLeft: 4 }}>(#{record.blockedById})</span>
        </>
      ),
    },
    {
      title: t('blockedUsers.blockedUser'), dataIndex: 'blockedUserId', sorter: true,
      render: (_: any, record: any) => (
        <>
          <span style={{ fontWeight: 500 }}>{record.blockedUserName || '—'}</span>
          <span style={{ color: '#999', marginLeft: 4 }}>(#{record.blockedUserId})</span>
        </>
      ),
    },
    {
      title: t('common.createdAt'), dataIndex: 'createdAt', sorter: true,
      render: (v: string) => formatDateTime(v),
    },
    {
      title: t('common.actions'), width: 80,
      render: (_: any, record: any) => (
        <Space>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} onClick={() => onDelete(record)} />
        </Space>
      ),
    },
  ];

  const handleChange: TableProps['onChange'] = (_pagination, _filters, sorter: any) => {
    if (sorter?.field) onSort(sorter.field);
  };

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      onChange={handleChange}
      sortDirections={['ascend', 'descend']}
      showSorterTooltip={false}
      pagination={{
        current: page + 1,
        pageSize,
        total,
        showTotal: (total) => `${total} items`,
        showSizeChanger: false,
        onChange: (p) => onPageChange(p - 1),
      }}
    />
  );
}
